<template>
    <!-- Alpha Access: Use Guardian-style design component -->
    <NewGuardianChat
        v-if="$userStore?.hasAlphaAccess && !disableAlphaDesign"
        :guardian="pattyGuardian"
        :show-history-btn="showHistoryBtn"
        :use-events="true"
        @submit="onGuardianSubmit"
        @open-history="openChatHistory"
    />

    <!-- Non-Alpha Access: Original design -->
    <div v-else class="new-chat-container">
        <div
            class="content-card-patty d-flex flex-column align-items-center justify-content-center"
        >
            <div class="robot-container">
                <DotLottieVue
                    :data="
                        require('@/assets/images/patty/lottie/patty-floating.json')
                    "
                    class="patty-floating"
                    loop
                    autoplay
                />
            </div>

            <h2 class="text-center m-0">Ask Patty</h2>

            <form
                @submit.prevent="onNewChatSubmit"
                class="w-100 d-flex flex-column"
                style="gap: var(--space-4)"
            >
                <p class="text-muted font-body m-0">
                    <b>Hi {{ userName }}!</b> I can assist with any questions
                    you have about IPAC.
                </p>

                <Field
                    label="Ask Patty a question"
                    type="textarea"
                    v-model="messageInput"
                    :disabled="isViewOnlyMode"
                    :params="{
                        tooltipContent:
                            'Search for all of your IPAC related questions',
                        placeholder: isViewOnlyMode
                            ? 'Upgrade to ask Patty a question'
                            : 'Type your question here.\n\nInstantly search through all your documents and find exactly what you need!\n\nE.g. What do I need to do when my employee does not wear a mask?',
                    }"
                    @keydown.enter.prevent="triggerFormSubmit"
                />

                <div class="d-flex justify-content-center">
                    <button
                        type="submit"
                        class="w-100 btn btn-secondary"
                        ref="sendButton"
                        :disabled="disableAskPattyBtn"
                    >
                        <template v-if="isViewOnlyMode">
                            <i class="fi fi-lock"></i>
                            {{ this.isViewOnlyMode && !this.$userStore.isCompanyAdmin ? '' : 'Unlock to Ask Patty' }}
                        </template>
                        <template v-else>
                            Ask Patty
                            <i class="fi fi-chevron-right"></i>
                        </template>
                    </button>
                </div>
            </form>

            <a
                v-if="showHistoryBtn"
                class="text-link"
                style="cursor: pointer"
                @click="openChatHistory"
            >
                View Your Chat History
            </a>
        </div>
    </div>
</template>

<script>
import Field from '@/components/Field.vue';
import { usePattyStore } from '@/stores/patty';
import { DotLottieVue } from '@lottiefiles/dotlottie-vue';
import NewGuardianChat from '@/components/guardians/NewGuardianChat.vue';
import pattyWaveImg from '@/assets/images/patty/guardian/patty-wave.svg';

export default {
    name: 'NewPattyChat',
    components: {
        Field,
        DotLottieVue,
        NewGuardianChat,
    },
    props: {
        showHistoryBtn: {
            type: Boolean,
            required: false,
            default: true,
        },
        disableAlphaDesign: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            messageInput: '',
            loading: false,

            pattyStore: null,
        };
    },
    computed: {
        // User data from store
        userName() {
            return this.$userStore.getFullName || '';
        },
        isViewOnlyMode() {
            return this.$userStore.isViewOnly;
        },
        disableAskPattyBtn() {
            if (this.isViewOnlyMode && !this.$userStore.isCompanyAdmin) {
                return true;
            }
            if (!this.isViewOnlyMode && !this.messageInput) {
                return true;
            }
            return false;
        },
        pattyGuardian() {
            return {
                id: 'patty',
                name: 'Patty',
                label: 'IPAC',
                agentType: 'PATTY',
                waveImage: pattyWaveImg,
                description: 'I can assist with any questions you have about IPAC.',
            };
        },
    },
    methods: {
        adjustTextareaHeight() {
            if (!this.$el || typeof this.$el.querySelector !== 'function') {
                return;
            }
            const textarea = this.$el.querySelector('textarea');
            if (textarea) {
                textarea.style.height = 'auto'; // Reset height to auto first
                textarea.style.height =
                    Math.min(textarea.scrollHeight + 3, 200) + 'px'; // Set to scrollHeight, but capped at 240px (max-height)
            }
        },
        openChatHistory() {
            this.pattyStore.openHistory();
        },
        onNewChatSubmit() {
            if (this.isViewOnlyMode) {
                this.$userStore.togglePremiumModal(true);
                return;
            }
            this.loading = true;
            const queryText = this.messageInput.trim();
            this.messageInput = '';
            this.pattyStore.openThread(queryText);
        },
        onGuardianSubmit(queryText) {
            this.loading = true;
            this.pattyStore.openThread(queryText);
        },
        triggerFormSubmit() {
            this.$refs.sendButton.click();
        },
    },
    mounted() {
        this.pattyStore = usePattyStore();

        // Only adjust textarea height if showing original design (not NewGuardianChat)
        if (!this.$userStore?.hasAlphaAccess || this.disableAlphaDesign) {
            this.adjustTextareaHeight();
            window.addEventListener('resize', this.adjustTextareaHeight);
        }
    },
    beforeUnmount() {
        if (!this.$userStore?.hasAlphaAccess || this.disableAlphaDesign) {
            window.removeEventListener('resize', this.adjustTextareaHeight);
        }
    },
};
</script>

<style scoped>
.content-card-patty {
    padding: var(--space-5);
    padding-top: 144px;
    position: relative;
    gap: var(--space-3);
}
.new-chat-container {
    background-color: var(--primary-25);
    border: 1px solid var(--primary-50);
    border-radius: var(--radius-lg);
}

.robot-container {
    position: absolute;
    width: 10.8125rem;
    height: 11.0625rem;
    top: -2rem;
    left: 50%;
    transform: translateX(-50%);
}

.patty-floating {
    width: 100%;
    height: 100%;
}

.btn:disabled {
    pointer-events: none;
    cursor: not-allowed;
}
</style>
